import { Routes } from '@angular/router';
import { DummyComponent } from '@app/dummy/dummy.component';
import { ShellComponent } from '@app/shell/shell.component';
import { authProviders } from '@auth/constants/auth-provider.constant';
import { ResetContentGuard } from '@auth/guards/reset-content.guard';
import { UserActiveGuard } from '@auth/guards/user-active.guard';
import { UserFeatureGuard } from '@auth/guards/user-feature.guard';
import { MsalGuard } from '@azure/msal-angular';
import { menu } from '@core/constants/menu-items.constant';
import { SigningInComponent } from '@core/containers/signing-in/signing-in.component';
import { AppRootRoute } from '@core/enums/app-route.enum';
import { AppConfig } from '@core/models/config.models';
import { Permission } from '@core/services/permission.model';
import { OktaAuthGuard } from '@okta/okta-angular';

function getShellRoutes(config: AppConfig): Routes {
  const canMatchAuthGuard: any[] =
    config.auth.provider === authProviders.Entra ? [MsalGuard] : [];
  const canActiveAuthGuard =
    config.auth.provider === authProviders.Entra ? MsalGuard : OktaAuthGuard;

  return [
    {
      path: 'change-role/:roleId',
      component: DummyComponent,
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: 'delete-file/:fileId',
      component: DummyComponent,
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: AppRootRoute.HOME,
      loadChildren: () =>
        import('./home/home.module').then(m => {
          return m.HomeModule;
        }),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: AppRootRoute.ADMIN_PROCESSES,
      loadChildren: () =>
        import('./processes/processes.module').then(
          m => m.AdminProcessesModule
        ),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.WRITE,
      },
    },
    {
      path: AppRootRoute.PROCESSES,
      loadChildren: () =>
        import('./processes/processes.module').then(m => m.ProcessesModule),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: AppRootRoute.ADMIN_SEARCH_KEYWORDS,
      loadChildren: () =>
        import('./search-keyword/search-keyword.module').then(
          m => m.SearchKeywordModule
        ),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      canDeactivate: [ResetContentGuard],
      data: {
        permission: Permission.ADMIN,
      },
    },
    {
      path: AppRootRoute.ADMIN_LANGUAGES,
      loadChildren: () =>
        import('./tenant-language/tenant-language.module').then(
          m => m.TenantLanguageModule
        ),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      canDeactivate: [ResetContentGuard],
      data: {
        permission: Permission.ADMIN,
      },
    },
    {
      path: AppRootRoute.CONTENT_CATEGORIES,
      loadChildren: () =>
        import('./content-category/content-category.module').then(
          m => m.ContentCategoryModule
        ),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      canDeactivate: [ResetContentGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: AppRootRoute.ADMIN_CONTENT_CATEGORIES,
      loadChildren: () =>
        import('./content-category/content-category-routing.module').then(
          m => m.ContentCategoryAdminRoutingModule
        ),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      canDeactivate: [ResetContentGuard],
      data: {
        permission: Permission.WRITE,
      },
    },
    {
      path: AppRootRoute.ADMIN_FILES,
      loadChildren: () =>
        import('./tenant-file/tenant-file.module').then(
          m => m.TenantFileModule
        ),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      canDeactivate: [ResetContentGuard],
      data: {
        permission: Permission.WRITE,
      },
    },
    {
      path: AppRootRoute.NEWS,
      loadChildren: () => import('./news/news.module').then(m => m.NewsModule),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: AppRootRoute.ADMIN_NEWS,
      loadChildren: () =>
        import('./news/news-routing.module').then(
          m => m.NewsAdminRoutingModule
        ),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.WRITE,
      },
    },
    {
      path: AppRootRoute.WIKIS,
      loadChildren: () =>
        import('./wiki-page/wiki-page.module').then(m => m.WikiPageModule),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: AppRootRoute.ADMIN_WIKIS,
      loadChildren: () =>
        import('./wiki-page/wiki-page.module').then(m => m.AdminWikiPageModule),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.WRITE,
      },
    },
    {
      path: AppRootRoute.ADMIN_QUICKLINKS,
      loadChildren: () =>
        import('./quicklink/quicklink.module').then(m => m.QuicklinkModule),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.WRITE,
      },
    },
    {
      path: AppRootRoute.TICKETS,
      loadChildren: () =>
        import('./ticket/ticket.module').then(m => m.TicketModule),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: menu.ticketOverview.state.permission,
        rootRoute: AppRootRoute.TICKETS,
      },
    },
    {
      path: AppRootRoute.ADMIN_TICKETS,
      loadChildren: () =>
        import('./ticket/ticket.module').then(m => m.TicketAdminModule),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: menu.ticketManagement.state.permission,
        rootRoute: AppRootRoute.ADMIN_TICKETS,
      },
    },
    {
      path: AppRootRoute.NOTIFICATIONS,
      loadChildren: () =>
        import('./notification/notification.module').then(
          m => m.NotificationModule
        ),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: AppRootRoute.PROFILE,
      loadChildren: () =>
        import('./profile/profile.module').then(m => {
          return m.ProfileModule;
        }),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: menu.dashboard.state.permission,
        type: AppRootRoute.PROFILE,
      },
    },
    {
      path: AppRootRoute.SEARCH,
      loadChildren: () =>
        import('./search/search.module').then(m => {
          return m.SearchModule;
        }),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
        type: AppRootRoute.SEARCH,
      },
    },
    {
      path: AppRootRoute.ERRORS,
      loadChildren: () =>
        import('./generic-page/generic-page.module').then(m => {
          return m.GenericPageModule;
        }),
      canMatch: canMatchAuthGuard.concat([UserFeatureGuard]),
      canActivate: [canActiveAuthGuard, UserFeatureGuard],
      data: {
        permission: Permission.READ,
      },
    },
    {
      path: '',
      redirectTo: `/${AppRootRoute.HOME}`,
      pathMatch: 'full',
    },
  ];
}

export function getRoutes(config: AppConfig): Routes {
  const canMatchAuthGuard: any[] =
    config.auth.provider === authProviders.Entra ? [MsalGuard] : [];
  const canActiveAuthGuard =
    config.auth.provider === authProviders.Entra ? MsalGuard : OktaAuthGuard;

  return [
    {
      path: '',
      component: ShellComponent,
      children: getShellRoutes(config),
    },
    {
      path: AppRootRoute.DOCUMENT,
      loadChildren: () =>
        import('./document/document.module').then(m => {
          return m.DocumentModule;
        }),
      canMatch: canMatchAuthGuard,
      canActivate: [canActiveAuthGuard],
    },
    {
      path: `${AppRootRoute.SIGN_IN}`,
      component: SigningInComponent,
      canMatch: canMatchAuthGuard.concat([UserActiveGuard]),
      canActivate: [canActiveAuthGuard, UserActiveGuard],
    },
    {
      path: '**',
      redirectTo: `/${AppRootRoute.ERRORS}/${AppRootRoute.FORBIDDEN}`,
      pathMatch: 'full',
    },
  ];
}
